/**
 * MediaRecorder + VAD for ap.orchestrator / Whisper transcription (same behavior as trufront useVoiceRecorder).
 * Vue 2–friendly: pass a reactive `state` object from component data { isRecording, error, isSupported }.
 */

const DEFAULT_SILENCE_MS = 1200
const DEFAULT_SILENCE_THRESHOLD = 0.01
const VAD_CHECK_INTERVAL_MS = 100

function checkSupport() {
	if (typeof navigator === 'undefined') return false
	return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder)
}

/**
 * @param {{ isRecording: boolean, error: string|null, isSupported: boolean }} state
 */
export default function createVoiceRecorderApi(state) {
	let mediaRecorder = null
	let stream = null
	let audioContext = null
	let analyser = null
	let vadCheckInterval = null
	let silenceStartTime = null
	let hasDetectedSpeech = false
	const chunks = []

	state.isSupported = checkSupport()

	function getAudioLevel() {
		if (!analyser) return 0
		const data = new Uint8Array(analyser.frequencyBinCount)
		analyser.getByteFrequencyData(data)
		const sum = data.reduce((a, b) => a + b, 0)
		return sum / data.length / 255
	}

	function startVAD(onAutoStop, silenceMs) {
		if (!stream || !onAutoStop) return

		audioContext = new (window.AudioContext || window.webkitAudioContext)()
		analyser = audioContext.createAnalyser()
		analyser.fftSize = 2048
		analyser.smoothingTimeConstant = 0.8

		const source = audioContext.createMediaStreamSource(stream)
		source.connect(analyser)

		silenceStartTime = null
		hasDetectedSpeech = false
		const duration = silenceMs != null ? silenceMs : DEFAULT_SILENCE_MS

		vadCheckInterval = setInterval(() => {
			const level = getAudioLevel()
			const isSilent = level < DEFAULT_SILENCE_THRESHOLD

			if (!isSilent) {
				hasDetectedSpeech = true
				silenceStartTime = null
				return
			}

			if (!hasDetectedSpeech) return

			const now = Date.now()
			if (!silenceStartTime) {
				silenceStartTime = now
			} else if (now - silenceStartTime >= duration) {
				clearInterval(vadCheckInterval)
				vadCheckInterval = null
				onAutoStop()
			}
		}, VAD_CHECK_INTERVAL_MS)
	}

	function stopVAD() {
		if (vadCheckInterval) {
			clearInterval(vadCheckInterval)
			vadCheckInterval = null
		}
		if (audioContext) {
			audioContext.close().catch(() => {})
			audioContext = null
		}
		analyser = null
	}

	function stop() {
		return new Promise((resolve) => {
			if (!mediaRecorder || !state.isRecording) {
				stopVAD()
				resolve(null)
				return
			}

			stopVAD()

			const mimeType = mediaRecorder.mimeType || 'audio/webm'
			mediaRecorder.onstop = () => {
				state.isRecording = false
				if (stream && stream.getTracks) {
					stream.getTracks().forEach(t => t.stop())
				}
				stream = null
				mediaRecorder = null

				if (chunks.length === 0) {
					resolve(null)
					return
				}
				const blob = new Blob(chunks, { type: mimeType })
				resolve(blob)
			}
			mediaRecorder.stop()
		})
	}

	async function start(options = {}) {
		if (!state.isSupported) {
			state.error = 'Voice recording is not supported in this browser'
			return false
		}

		const { onAutoStop, silenceMs = DEFAULT_SILENCE_MS } = options
		state.error = null
		chunks.length = 0

		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true })
			let mimeType = 'audio/mp4'
			if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
				mimeType = 'audio/webm;codecs=opus'
			} else if (MediaRecorder.isTypeSupported('audio/webm')) {
				mimeType = 'audio/webm'
			}
			mediaRecorder = new MediaRecorder(stream, { mimeType })
			mediaRecorder.ondataavailable = (e) => {
				if (e.data.size > 0) chunks.push(e.data)
			}
			mediaRecorder.start(100)
			state.isRecording = true

			if (typeof onAutoStop === 'function') {
				startVAD(async () => {
					stopVAD()
					const blob = await stop()
					if (blob) onAutoStop(blob)
				}, silenceMs)
			}

			return { mimeType: mimeType.split(';')[0] }
		} catch (e) {
			state.error = (e && e.message) || 'Failed to access microphone'
			state.isRecording = false
			return false
		}
	}

	function reset() {
		stopVAD()
		if (mediaRecorder && state.isRecording) {
			try {
				mediaRecorder.stop()
			} catch (_) { /* already stopped */ }
			if (stream && stream.getTracks) {
				stream.getTracks().forEach(t => t.stop())
			}
		}
		stream = null
		mediaRecorder = null
		state.isRecording = false
	}

	async function blobToBase64(blob) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.onloadend = () => {
				const base64 = reader.result && reader.result.split(',')[1]
				resolve(base64 || '')
			}
			reader.onerror = reject
			reader.readAsDataURL(blob)
		})
	}

	function dispose() {
		reset()
	}

	return {
		start,
		stop,
		reset,
		blobToBase64,
		dispose,
	}
}
